import { Injectable, inject } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { RichVerifyDialogComponent } from '@app/modules/dialogs/rich-verify-phone-dialog/rich-verify-dialog.component';
import { LoginResponse, UsersService } from '@app/services/users.service';
import { TokensService } from '@app/services/tokens.service';
import { ContactsService } from '@app/services/contacts.service';
import { HttpErrorResponse } from '@angular/common/http';
import { RecaptchaService } from '@app/services/recaptcha-service';
import { DialogService } from '@app/modules/dialogs/dialog.service';

export interface PhoneInterface {
    phoneCountryCode: string;
    phoneNumber: string;
}

export interface VerificationResponse {
    _id: string; // token
    email: string;
    userId: string;
}

@Injectable({
    providedIn: 'root',
})
export class AuthenticateByPhoneService {
    private readonly userService = inject(UsersService);
    private readonly tokenService = inject(TokensService);
    private readonly contactService = inject(ContactsService);
    private readonly recaptchaService = inject(RecaptchaService);
    private readonly dialogService = inject(DialogService);

    public async login(phone: PhoneInterface): Promise<LoginResponse> {
        let response: LoginResponse = { authenticated: false };
        let token: string;
        let userId: string;
        const gcToken = await this.recaptchaService.check('tokens');
        try {
            const response = await this.sendVerificationSms(phone, gcToken);
            token = response._id;
            userId = response.userId;
        } catch (e) {
            if (e instanceof HttpErrorResponse) {
                switch (e.status) {
                    case 403: // Blacklisted
                    case 404: // Not found
                    case 422: // Unprocessable - captcha error
                    case 429: // Too many requests
                        return { authenticated: false, errorCode: e.status, errorMessage: e.message };
                }
            }
            console.error('AuthenticateByPhoneService', e);
            throw e;
        }

        const dialogRef: MatDialogRef<RichVerifyDialogComponent> = this.dialogService.richVerify();
        dialogRef.componentInstance.phone = phone;

        let phoneCode = null;

        // 3 attempts for input verification code
        for (let i = 0; i < 3; i++) {
            const authenticationPhoneCode: string = await dialogRef.componentInstance.submitPromiseWithCode;
            if (authenticationPhoneCode === null) {
                return { authenticated: false };
            }

            try {
                response = await this.userService.loginPasswordLessByUserId(userId, token, authenticationPhoneCode);
                if (response.authenticated) {
                    phoneCode = authenticationPhoneCode;
                    dialogRef.close(true);

                    const gcToken = await this.recaptchaService.check('contacts/verify');
                    await this.contactService.verifyCaptcha({
                        contactId: this.userService.user.contactId,
                        phoneCode,
                        gcToken,
                    });

                    return response;
                }
            } catch (e) {
                console.error(e);
            }

            dialogRef.componentInstance.verifiedState = false;
            dialogRef.componentInstance.resetDialog();
        }

        dialogRef.close(false);
        return { authenticated: false };
    }

    private async sendVerificationSms(phone: PhoneInterface, gcToken: string): Promise<VerificationResponse> {
        return this.tokenService.create({ phone, gcToken });
    }
}
